import { useTranslate } from "@refinedev/core";
import { Button, Card, Col, Result, Row, theme } from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { axiosInstance } from "../../authProvider";

const { useToken } = theme;

type ActivationState =
  | { kind: "loading" }
  | { kind: "activated"; tenantName: string; trialEndsAt: string | null }
  | { kind: "expired" }
  | { kind: "invalid" };

/**
 * Landing page for the activation link emailed at signup. Distinct from
 * /confirm-email: different endpoint, a `token` query param rather than `hash`, and
 * three outcomes rather than two.
 */
const ActivateTenant = () => {
  const _t = useTranslate();
  const { token: themeToken } = useToken();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [state, setState] = useState<ActivationState>({ kind: "loading" });
  // React 18 StrictMode mounts effects twice in dev; without this the token would be
  // consumed by the first call and the second would report it invalid.
  const hasCalledApi = useRef(false);

  useEffect(() => {
    const activate = async () => {
      const token = params.get("token");
      if (!token) {
        setState({ kind: "invalid" });
        return;
      }
      if (hasCalledApi.current) {
        return;
      }
      hasCalledApi.current = true;

      try {
        const response = await axiosInstance.post("/tenant-signup/activate", {
          token,
        });
        setState({
          kind: "activated",
          tenantName: response.data?.tenantName ?? "",
          trialEndsAt: response.data?.trialEndsAt ?? null,
        });
      } catch (error: any) {
        const code = error?.response?.data?.errors?.token;
        setState({ kind: code === "tokenExpired" ? "expired" : "invalid" });
      }
    };
    activate();
  }, [params]);

  const renderResult = () => {
    switch (state.kind) {
      case "loading":
        return (
          <Result
            status="info"
            title={_t("pages.tenantSignup.activate.loadingTitle")}
            subTitle={_t("pages.tenantSignup.activate.loadingSubTitle")}
          />
        );
      case "activated":
        return (
          <Result
            status="success"
            title={_t("pages.tenantSignup.activate.successTitle")}
            subTitle={
              state.trialEndsAt
                ? _t("pages.tenantSignup.activate.successSubTitle", {
                    date: dayjs(state.trialEndsAt).format("DD/MM/YYYY"),
                  })
                : undefined
            }
            extra={
              <Button type="primary" onClick={() => navigate("/login")}>
                {_t("pages.register.gotoLoginMessage")}
              </Button>
            }
          />
        );
      case "expired":
        return (
          <Result
            status="warning"
            title={_t("pages.tenantSignup.activate.expiredTitle")}
            subTitle={_t("pages.tenantSignup.activate.expiredSubTitle")}
            extra={
              <Button
                type="primary"
                onClick={() => navigate("/resend-activation")}
              >
                {_t("pages.tenantSignup.activate.requestNew")}
              </Button>
            }
          />
        );
      case "invalid":
        return (
          <Result
            status="error"
            title={_t("pages.tenantSignup.activate.errorTitle")}
            subTitle={_t("pages.tenantSignup.activate.errorSubTitle")}
            extra={
              <Button
                type="primary"
                onClick={() => navigate("/resend-activation")}
              >
                {_t("pages.tenantSignup.activate.requestNew")}
              </Button>
            }
          />
        );
    }
  };

  return (
    <Row
      justify="center"
      align="middle"
      style={{ minHeight: "100vh", backgroundColor: themeToken.colorBgLayout }}
    >
      <Col className="sm:w-100 md:w-1/2">
        <Card
          className="mt-16 text-center"
          style={{
            backgroundColor: themeToken.colorBgContainer,
            borderColor: themeToken.colorBorder,
          }}
        >
          {renderResult()}
        </Card>
      </Col>
    </Row>
  );
};

export default ActivateTenant;
